import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, WritableSignal } from '@angular/core';
import { DriverInformationService, FilterService } from '../../../../_shared/services';
import { DriverInformationPaginationRequest } from '../../../../_shared/models/drivers/management/driver-information-pagination-request.mode';
import { DriverInformation } from '../../models/driver-information.model';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { DriverInformationListService } from '../../services/driver-information-list.service';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AvatarComponent } from "../../../../_shared/components/avatar/avatar.component";
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DriverDocumentNotificationResult } from '../../../../_shared/models/drivers/management/driver-document-notification.mode';
import { notificationHandler } from '../../../../_shared/helpers/notification-handler.helper';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-information-list',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, AvatarComponent, NgbTooltip],
  templateUrl: './driver-information-list.component.html',
  styleUrl: './driver-information-list.component.scss',
})
export class DriverInformationListComponent implements OnInit {
  @Input() hasDrivers: WritableSignal<boolean | null>;
  @Output() loaded = new EventEmitter<boolean>();

  public driversInformation: any[] = [];
  public isLoading: boolean = false;
  public hasMoreData: boolean = true;
  public selectedIndex: number | null = null;
  public isFirstSelected: boolean = false;
  public filterSubscription: Subscription;
  public notificationMessage: string = '';
  public notificationCount: number = 0;
  public searchControl = new FormControl('');

  private previouslySelectedDriver: any | null = null;
  private cancellation = new Subject<void>();
  private searchSubscription: Subscription;
  private hasDriversOnInit: boolean = false;
  private driverInformationService = inject(DriverInformationService);
  private filterService = inject(FilterService);
  private driverInformationList = inject(DriverInformationListService);
  private cdr = inject(ChangeDetectorRef);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  paginatedRequest: DriverInformationPaginationRequest = {
    accountIds: [],
    page: 1,
    pageSize: 20,
    searchTerm: ''
  };

  ngOnInit(): void {
    this.subscribeToFilters();

    this.driverInformationList.refreshList$.subscribe(() => {
      this.resetPagination();
      this.loaded.emit(false);
      this.getDriversInformationList();
    });
    this.subscribeToSearch();
  }

  private subscribeToFilters(): void {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe(filter => {

      if (!filter || Object.values(filter).every(value => value == null)) {
        this.driverInformationList.updateSelectedDriver(null);
        return;
      }

      this.resetPagination();
      this.paginatedRequest.accountIds = filter.accountIds;
      this.getDriversInformationList();
    });
  }

  private subscribeToSearch(): void {
    this.searchSubscription = this.driverInformationList.searchTerm$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe(async searchTerm => {
        this.paginatedRequest.page = 1;
        this.paginatedRequest.searchTerm = searchTerm;
        this.resetPagination();
        
        await this.getDriversInformationList();
      });
  }

  private resetPagination(): void {
    this.paginatedRequest.page = 1;
    this.hasMoreData = true;
    this.driversInformation = [];
    this.selectedIndex = null;
  }

  public async getDriversInformationList() {
    if (this.isLoading || !this.hasMoreData) {
      return;
    }
    
    this.isLoading = true;
    try {
      if (this.selectedIndex !== null && this.paginatedRequest.page === 1) {
        this.previouslySelectedDriver = this.driversInformation[this.selectedIndex]?.id || null;
      }

      this.cancellation.next();
      const response = await this.driverInformationService.getDriversListInformationDetail(this.paginatedRequest, this.cancellation);
      const newDrivers = this.mapDriverData(response?.data?.results ?? []);

      this.driversInformation = [...this.driversInformation, ...newDrivers];

      if (this.driversInformation.length > 0) {
        if (this.paginatedRequest.page === 1) {
          this.restoreSelection(this.driversInformation);
        }
        this.driverInformationList.updateDrivers(this.driversInformation);
      } else {
        this.clearSelection();
      }

      this.hasMoreData = newDrivers.length === this.paginatedRequest.pageSize;
      this.checkHasDrivers();

      if (this.hasMoreData) {
        this.paginatedRequest.page++;
      }

      this.isLoading = false;
      this.cdr.detectChanges();
      this.loaded.emit(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.cdr.detectChanges();
          this.loaded.emit(true);
        }
      });
    }
  }

  private checkHasDrivers(): void {
    if (this.hasDrivers() === null) {
      const hasDrivers = this.driversInformation.length > 0;
      this.hasDrivers.set(hasDrivers);
      this.hasDriversOnInit = hasDrivers;
    } else if (this.driversInformation.length > 0 || (this.driversInformation.length === 0 && this.hasDriversOnInit)) {
      this.hasDrivers.set(true);
    } else {
      this.hasDrivers.set(false);
    }
  }

  private restoreSelection(driversInformation: DriverInformation[]): void {
    if (this.previouslySelectedDriver) {
      const newIndex = driversInformation.findIndex(driver => driver.id === this.previouslySelectedDriver?.id);
      if (newIndex !== -1) {
        this.selectDriver(driversInformation[newIndex], newIndex);
        return;
      }
    }
    this.selectDriver(driversInformation[0], 0);
  }

  private clearSelection(): void {
    this.driverInformationList.updateSelectedDriver(null);
    this.driverInformationList.updateDrivers(null);
    this.selectedIndex = null;
  }

  private mapDriverData(drivers: DriverInformation[]): any {
    return drivers.map(driver => ({
      id: driver.id,
      name: `${driver.firstName} ${driver.lastName}`,
      accountName: driver?.accountName ?? this.transloco.translate('common.unknown'),
      image: driver?.profilePictureUrl,
      driverLicenseRenewal: driver.driverLicenseRenewal,
      professionalDriversPermitRenewalDate: driver.professionalDriversPermitRenewalDate
    }));
  }

  public selectDriver(driver: any | null, selectedIndex: number | null): void {
    this.selectedIndex = selectedIndex;
    this.previouslySelectedDriver = driver;

    if (!driver) {
      this.driverInformationList.updateSelectedDriver(null);
      return;
    }
    this.driverInformationList.updateSelectedDriver(driver);
  }

  public notificationHandler(licenseDate: Date | string, pdpDate: Date | string): DriverDocumentNotificationResult {
    const notification = notificationHandler(licenseDate, pdpDate);
    this.notificationMessage = notification?.message ?? '';
    this.notificationCount = notification?.count ?? 0;

    return notification;
  }

  onScrollDown(): void {
    this.getDriversInformationList();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();

    this.cancellation.next();
    this.cancellation.complete();
  }
}